import { useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Settings as SettingsType,
  getSettings,
  patchSettings,
  bootstrapKeywords,
  getMetaOAuthStart,
  uploadLogo,
} from "../lib/api";
import Button from "../ui/Button";
import Switch from "../ui/Switch";
import Spinner from "../ui/Spinner";
import { TabsRail } from "../ui/Tabs";
import { useAsyncAction } from "../ui/useAsyncAction";
import { useToast } from "../ui/toast";
import {
  IconChart,
  IconGlobe,
  IconImage,
  IconKey,
  IconMail,
  IconMegaphone,
  IconRefresh,
  IconSend,
  IconSparkles,
  IconTags,
  IconUpload,
} from "../ui/icons";
import UsagePanel from "../components/UsagePanel";

type TabKey =
  | "api-keys"
  | "wordpress"
  | "social"
  | "generation"
  | "notifications"
  | "email-list"
  | "logo"
  | "keywords"
  | "usage";

const DEFAULT_TAB: TabKey = "api-keys";

const TABS: { key: TabKey; label: string; icon: ReactNode }[] = [
  { key: "api-keys", label: "API keys", icon: <IconKey size={15} /> },
  { key: "wordpress", label: "WordPress", icon: <IconGlobe size={15} /> },
  { key: "social", label: "Social accounts", icon: <IconMegaphone size={15} /> },
  { key: "generation", label: "Generation", icon: <IconSparkles size={15} /> },
  { key: "notifications", label: "Notifications", icon: <IconMail size={15} /> },
  { key: "email-list", label: "Email list", icon: <IconSend size={15} /> },
  { key: "logo", label: "Brand logo", icon: <IconImage size={15} /> },
  { key: "keywords", label: "Keywords", icon: <IconTags size={15} /> },
  { key: "usage", label: "Usage", icon: <IconChart size={15} /> },
];

export default function Settings() {
  const [settings, setSettings] = useState<SettingsType | null>(null);
  const [params, setParams] = useSearchParams();
  const toast = useToast();

  const tab = (params.get("tab") as TabKey) || DEFAULT_TAB;
  const setTab = (key: TabKey) => {
    const next = new URLSearchParams(params);
    next.set("tab", key);
    setParams(next, { replace: true });
  };

  const load = async () => {
    try {
      setSettings(await getSettings());
    } catch (e) {
      toast.error(
        "Couldn't load settings",
        e instanceof Error ? e.message : String(e)
      );
    }
  };

  useEffect(() => {
    load();
    if (params.get("meta_connected")) {
      toast.success("Facebook + Instagram connected");
      const next = new URLSearchParams(params);
      next.delete("meta_connected");
      setParams(next, { replace: true });
    }
    const metaErr = params.get("meta_error");
    if (metaErr) {
      toast.error("Facebook connection failed", metaErr);
      const next = new URLSearchParams(params);
      next.delete("meta_error");
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!settings) {
    return (
      <div className="flex items-center gap-2 text-ink-500">
        <Spinner size={14} /> Loading settings…
      </div>
    );
  }

  const update = (body: Record<string, unknown>) =>
    patchSettings(body).then(setSettings);

  return (
    <div>
      <header className="mb-8">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-ink-800 tracking-tight">
          Settings
        </h1>
        <p className="text-sm text-ink-500 mt-1.5">
          API keys, integrations, and pipeline configuration. All keys are
          encrypted at rest.
        </p>
      </header>

      <div className="flex gap-10 items-start">
        <TabsRail
          tabs={TABS}
          active={tab}
          onChange={(k) => setTab(k as TabKey)}
        />
        <div className="flex-1 min-w-0 max-w-3xl">
          {tab === "api-keys" && (
            <ApiKeysPanel settings={settings} update={update} />
          )}
          {tab === "wordpress" && (
            <WordPressPanel
              settings={settings}
              setSettings={setSettings}
              update={update}
            />
          )}
          {tab === "social" && <SocialPanel settings={settings} />}
          {tab === "generation" && (
            <GenerationPanel
              settings={settings}
              setSettings={setSettings}
              update={update}
            />
          )}
          {tab === "notifications" && (
            <NotificationsPanel
              settings={settings}
              setSettings={setSettings}
              update={update}
            />
          )}
          {tab === "email-list" && (
            <EmailListPanel settings={settings} update={update} />
          )}
          {tab === "logo" && (
            <BrandLogoPanel settings={settings} setSettings={setSettings} />
          )}
          {tab === "keywords" && <KeywordsPanel settings={settings} />}
          {tab === "usage" && <UsagePanel />}
        </div>
      </div>
    </div>
  );
}

/* ---------- Shared primitives ---------- */

function PanelCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="bg-white border border-ink-100 rounded-xl overflow-hidden shadow-sm shadow-ink-900/5">
      <header className="px-6 py-5 border-b border-ink-100">
        <h2 className="font-display text-lg font-semibold text-ink-800">
          {title}
        </h2>
        {description && (
          <p className="text-sm text-ink-500 mt-1">{description}</p>
        )}
      </header>
      <div className="p-6">{children}</div>
    </section>
  );
}

function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <label className="block text-xs font-semibold text-ink-600 uppercase tracking-wide mb-1.5">
      {children}
    </label>
  );
}

function TextInput(
  props: React.InputHTMLAttributes<HTMLInputElement>
) {
  return (
    <input
      {...props}
      className={[
        "w-full px-3 py-2 border border-ink-200 rounded-md text-sm",
        "focus:border-brand-500 focus:ring-0",
        props.className ?? "",
      ].join(" ")}
    />
  );
}

function MaskedKeyInput({
  label,
  isSet,
  last4,
  onSave,
}: {
  label: string;
  isSet: boolean;
  last4: string | null;
  onSave: (val: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const save = useAsyncAction(
    async (v: string) => {
      await onSave(v);
    },
    {
      successMessage: `${label} saved`,
      onSuccess: () => {
        setVal("");
        setEditing(false);
      },
    }
  );

  return (
    <div className="mb-5">
      <FieldLabel>{label}</FieldLabel>
      {!editing ? (
        <div className="flex items-center gap-3">
          <span
            className={[
              "inline-flex items-center gap-2 text-sm px-2.5 py-1.5 rounded-md border",
              isSet
                ? "border-mint-200 bg-mint-100/50 text-ink-800"
                : "border-ink-200 bg-ink-50 text-ink-500",
            ].join(" ")}
          >
            <span
              className={`h-2 w-2 rounded-full ${
                isSet ? "bg-mint-500" : "bg-ink-300"
              }`}
            />
            {isSet ? `Set · …${last4 ?? "****"}` : "Not set"}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditing(true)}
          >
            {isSet ? "Replace" : "Set"}
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <TextInput
            type="password"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder={`Enter ${label}`}
            className="flex-1"
          />
          <Button
            variant="primary"
            onClick={() => val && save.run(val)}
            pending={save.pending}
            disabled={!val}
          >
            {save.pending ? "Saving…" : "Save"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setEditing(false);
              setVal("");
            }}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}

/* ---------- Panels ---------- */

function ApiKeysPanel({
  settings,
  update,
}: {
  settings: SettingsType;
  update: (body: Record<string, unknown>) => Promise<void>;
}) {
  return (
    <PanelCard
      title="API keys"
      description="Keys are stored encrypted. You can only replace them, never view them again."
    >
      <MaskedKeyInput
        label="Anthropic API key"
        isSet={settings.anthropic_key.is_set}
        last4={settings.anthropic_key.last4}
        onSave={(v) => update({ anthropic_key: v })}
      />
      <MaskedKeyInput
        label="KIE AI API key"
        isSet={settings.kie_key.is_set}
        last4={settings.kie_key.last4}
        onSave={(v) => update({ kie_key: v })}
      />
      <MaskedKeyInput
        label="Tavily API key"
        isSet={settings.tavily_key.is_set}
        last4={settings.tavily_key.last4}
        onSave={(v) => update({ tavily_key: v })}
      />
    </PanelCard>
  );
}

function WordPressPanel({
  settings,
  setSettings,
  update,
}: {
  settings: SettingsType;
  setSettings: (s: SettingsType) => void;
  update: (body: Record<string, unknown>) => Promise<void>;
}) {
  return (
    <PanelCard
      title="WordPress"
      description="Where approved blogs get published. Use an Application Password, not your main account password."
    >
      <div className="mb-5">
        <FieldLabel>Site URL</FieldLabel>
        <TextInput
          type="url"
          value={settings.wordpress_url ?? ""}
          onChange={(e) =>
            setSettings({ ...settings, wordpress_url: e.target.value })
          }
          onBlur={() => update({ wordpress_url: settings.wordpress_url })}
          placeholder="https://www.tennisforlife.co.za"
        />
      </div>
      <div className="mb-5">
        <FieldLabel>Username</FieldLabel>
        <TextInput
          value={settings.wordpress_username ?? ""}
          onChange={(e) =>
            setSettings({ ...settings, wordpress_username: e.target.value })
          }
          onBlur={() =>
            update({ wordpress_username: settings.wordpress_username })
          }
        />
      </div>
      <MaskedKeyInput
        label="Application password"
        isSet={settings.wordpress_app_password.is_set}
        last4={settings.wordpress_app_password.last4}
        onSave={(v) => update({ wordpress_app_password: v })}
      />
      <div className="mt-5">
        <FieldLabel>Author username</FieldLabel>
        <TextInput
          value={settings.wordpress_author_username ?? ""}
          onChange={(e) =>
            setSettings({
              ...settings,
              wordpress_author_username: e.target.value,
            })
          }
          onBlur={() =>
            update({
              wordpress_author_username: settings.wordpress_author_username,
            })
          }
          placeholder="e.g. coach"
        />
        <p className="text-xs text-ink-500 mt-1">
          Which WordPress user is listed as the author on published blogs.
          We resolve this to the numeric user ID on save. Leave blank to
          use the account tied to the application password above.
          {settings.wordpress_author_username &&
            settings.wordpress_author_id === null && (
              <span className="text-amber-600">
                {" "}
                Could not find a matching user - double check the username.
              </span>
            )}
        </p>
      </div>
    </PanelCard>
  );
}

function SocialPanel({ settings }: { settings: SettingsType }) {
  const connect = useAsyncAction(
    async () => {
      const { url } = await getMetaOAuthStart();
      window.location.href = url;
    },
    { successMessage: null, loadingMessage: "Opening Facebook…" }
  );
  return (
    <PanelCard
      title="Facebook + Instagram"
      description="Connect the Facebook Page that owns your Instagram Business account. Both publish from this one connection."
    >
      {settings.meta.connected ? (
        <div className="mb-5 rounded-lg border border-mint-200 bg-mint-100/40 px-4 py-3">
          <p className="text-sm text-ink-800">
            <span className="font-medium">Connected.</span> Publishing as{" "}
            <strong>{settings.meta.page_name}</strong> (Page) +{" "}
            <strong>@{settings.meta.ig_username}</strong> (Instagram).
          </p>
        </div>
      ) : (
        <p className="text-sm text-ink-600 mb-4">
          Not connected yet. The Instagram account must be a{" "}
          <strong>Business</strong> account linked to the Facebook Page.
        </p>
      )}
      <Button
        variant={settings.meta.connected ? "secondary" : "primary"}
        onClick={() => connect.run()}
        pending={connect.pending}
      >
        {settings.meta.connected ? "Reconnect" : "Connect Facebook"}
      </Button>
    </PanelCard>
  );
}

function GenerationPanel({
  settings,
  setSettings,
  update,
}: {
  settings: SettingsType;
  setSettings: (s: SettingsType) => void;
  update: (body: Record<string, unknown>) => Promise<void>;
}) {
  return (
    <PanelCard
      title="Generation"
      description="How many social posts fan out per blog, when they publish, and whether the review agent can approve on its own."
    >
      <div className="flex gap-6 mb-6 flex-wrap">
        <div>
          <FieldLabel>Social posts per blog</FieldLabel>
          <TextInput
            type="number"
            min={1}
            max={14}
            value={settings.posts_per_blog}
            onChange={(e) =>
              setSettings({
                ...settings,
                posts_per_blog: parseInt(e.target.value || "1", 10),
              })
            }
            onBlur={() => update({ posts_per_blog: settings.posts_per_blog })}
            className="w-32"
          />
        </div>
        <div>
          <FieldLabel>Daily publish time</FieldLabel>
          <TextInput
            type="time"
            value={settings.social_publish_time}
            onChange={(e) =>
              setSettings({ ...settings, social_publish_time: e.target.value })
            }
            onBlur={() =>
              update({ social_publish_time: settings.social_publish_time })
            }
            className="w-40"
          />
        </div>
      </div>

      <div className="pt-4 border-t border-ink-100 mb-6">
        <BlogSchedulePicker
          settings={settings}
          setSettings={setSettings}
          update={update}
        />
      </div>

      <div className="space-y-4 pt-4 border-t border-ink-100">
        <Switch
          id="auto_blogs"
          checked={settings.auto_approve_blogs}
          onChange={(v) => update({ auto_approve_blogs: v })}
          label="Auto-approve blogs when review passes"
          description="Skip the manual approval step on blogs whose automated review comes back clean."
        />
        <Switch
          id="auto_social"
          checked={settings.auto_approve_social}
          onChange={(v) => update({ auto_approve_social: v })}
          label="Auto-approve social posts when review passes"
          description="Same, but for the social posts fanned out from each blog."
        />
      </div>
    </PanelCard>
  );
}

const WEEKDAYS: { num: number; label: string }[] = [
  { num: 1, label: "Mon" },
  { num: 2, label: "Tue" },
  { num: 3, label: "Wed" },
  { num: 4, label: "Thu" },
  { num: 5, label: "Fri" },
  { num: 6, label: "Sat" },
  { num: 0, label: "Sun" },
];

function BlogSchedulePicker({
  settings,
  setSettings,
  update,
}: {
  settings: SettingsType;
  setSettings: (s: SettingsType) => void;
  update: (body: Record<string, unknown>) => Promise<void>;
}) {
  const selected = settings.blog_schedule_days ?? [];

  const toggle = (num: number) => {
    const isOn = selected.includes(num);
    // Keep at least one day - an empty schedule is rejected by the API.
    if (isOn && selected.length === 1) return;
    const next = (
      isOn ? selected.filter((d) => d !== num) : [...selected, num]
    ).sort((a, b) => a - b);
    setSettings({ ...settings, blog_schedule_days: next });
    update({ blog_schedule_days: next });
  };

  const count = selected.length;

  return (
    <div>
      <FieldLabel>Blog schedule</FieldLabel>
      <div className="flex gap-1.5 flex-wrap">
        {WEEKDAYS.map((d) => {
          const on = selected.includes(d.num);
          return (
            <button
              key={d.num}
              type="button"
              onClick={() => toggle(d.num)}
              className={[
                "w-12 py-2 rounded-md text-sm font-semibold border transition-colors",
                on
                  ? "border-brand-500 bg-brand-500 text-white"
                  : "border-ink-200 bg-white text-ink-600 hover:border-ink-300",
              ].join(" ")}
            >
              {d.label}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-ink-500 mt-2">
        The generation cron creates one blog on each selected day —{" "}
        <strong>
          {count} blog{count === 1 ? "" : "s"} per week
        </strong>
        , early each morning in your configured timezone.
      </p>
    </div>
  );
}

function NotificationsPanel({
  settings,
  setSettings,
  update,
}: {
  settings: SettingsType;
  setSettings: (s: SettingsType) => void;
  update: (body: Record<string, unknown>) => Promise<void>;
}) {
  return (
    <PanelCard
      title="Email notifications"
      description="Get emailed when a blog publishes, or when one stalls needing manual review."
    >
      <MaskedKeyInput
        label="Resend API key"
        isSet={settings.resend_api_key.is_set}
        last4={settings.resend_api_key.last4}
        onSave={(v) => update({ resend_api_key: v })}
      />
      <div className="mb-5">
        <FieldLabel>From address</FieldLabel>
        <TextInput
          type="email"
          value={settings.resend_from_email ?? ""}
          onChange={(e) =>
            setSettings({ ...settings, resend_from_email: e.target.value })
          }
          onBlur={() => update({ resend_from_email: settings.resend_from_email })}
          placeholder="notifications@tennisforlife.co.za"
        />
        <p className="text-xs text-ink-500 mt-1">
          Must be a verified Resend domain.
        </p>
      </div>
      <div>
        <FieldLabel>Recipient emails</FieldLabel>
        <TextInput
          type="text"
          value={settings.notification_emails.join(", ")}
          onChange={(e) =>
            setSettings({
              ...settings,
              notification_emails: e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          onBlur={() =>
            update({ notification_emails: settings.notification_emails })
          }
          placeholder="info@tennisforlife.co.za, coach@tennisforlife.co.za"
        />
        <p className="text-xs text-ink-500 mt-1">
          Comma-separated. Leave empty to disable notifications.
        </p>
      </div>
    </PanelCard>
  );
}

function EmailListPanel({
  settings,
  update,
}: {
  settings: SettingsType;
  update: (body: Record<string, unknown>) => Promise<void>;
}) {
  const toast = useToast();
  const modes: {
    key: "off" | "draft" | "live";
    label: string;
    description: string;
  }[] = [
    {
      key: "off",
      label: "Off",
      description: "Published blogs are not sent to Kit.",
    },
    {
      key: "draft",
      label: "Draft (test formatting)",
      description:
        "Each published blog is created as a draft broadcast in Kit. Nothing is sent — open it in your Kit dashboard to preview, send yourself a test, and check the formatting.",
    },
    {
      key: "live",
      label: "Live (send to list)",
      description:
        "Each published blog is scheduled as a broadcast to your whole Kit subscriber list. Open/click analytics sync back to the dashboard daily.",
    },
  ];

  const setMode = async (mode: "off" | "draft" | "live") => {
    try {
      await update({ kit_send_mode: mode });
      toast.success(`Email list mode set to "${mode}"`);
    } catch (e) {
      toast.error(
        "Couldn't update mode",
        e instanceof Error ? e.message : String(e)
      );
    }
  };

  return (
    <PanelCard
      title="Kit email list"
      description="Send each published blog to your Kit (ConvertKit) subscriber list as an email broadcast. Works on the Kit free plan."
    >
      <MaskedKeyInput
        label="Kit API key (V4)"
        isSet={settings.kit_api_key.is_set}
        last4={settings.kit_api_key.last4}
        onSave={(v) => update({ kit_api_key: v })}
      />
      <p className="text-xs text-ink-500 -mt-3 mb-5">
        Create a V4 API key in Kit under Settings → Advanced → API. The key is
        stored encrypted.
      </p>

      <FieldLabel>Send mode</FieldLabel>
      <div className="space-y-2">
        {modes.map((m) => {
          const active = settings.kit_send_mode === m.key;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => !active && setMode(m.key)}
              className={[
                "w-full text-left rounded-lg border px-4 py-3 transition-colors",
                active
                  ? "border-brand-500 bg-brand-50"
                  : "border-ink-200 bg-white hover:border-ink-300",
              ].join(" ")}
            >
              <div className="flex items-center gap-2">
                <span
                  className={[
                    "h-3.5 w-3.5 rounded-full border-2 shrink-0",
                    active
                      ? "border-brand-500 bg-brand-500"
                      : "border-ink-300 bg-white",
                  ].join(" ")}
                />
                <span className="text-sm font-semibold text-ink-800">
                  {m.label}
                </span>
              </div>
              <p className="text-xs text-ink-500 mt-1 ml-[22px]">
                {m.description}
              </p>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-ink-500 mt-4">
        Recommended rollout: set the API key, choose <strong>Draft</strong> and
        publish a blog to review the email formatting in Kit, then switch to{" "}
        <strong>Live</strong> once you're happy.
      </p>
    </PanelCard>
  );
}

function BrandLogoPanel({
  settings,
  setSettings,
}: {
  settings: SettingsType;
  setSettings: (s: SettingsType) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const onFile = async (file: File) => {
    setUploading(true);
    try {
      const { brand_logo_url } = await uploadLogo(file);
      setSettings({ ...settings, brand_logo_url });
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error("Upload failed", e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <PanelCard
      title="Brand logo"
      description="A transparent PNG works best. Once uploaded you can apply it to any generated image in your chosen corner."
    >
      {settings.brand_logo_url ? (
        <div className="mb-5 inline-flex items-center gap-3 p-3 rounded-lg bg-ink-50 border border-ink-100">
          <img
            src={settings.brand_logo_url}
            alt="Brand logo"
            className="h-14 w-auto"
          />
          <span className="text-xs text-ink-500">Current logo</span>
        </div>
      ) : (
        <p className="text-sm text-ink-500 mb-4">No logo uploaded yet.</p>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/webp,image/svg+xml"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          await onFile(file);
          e.target.value = "";
        }}
        className="hidden"
      />
      <Button
        variant="secondary"
        iconLeft={<IconUpload size={14} />}
        pending={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {uploading ? "Uploading…" : "Upload logo"}
      </Button>
    </PanelCard>
  );
}

function KeywordsPanel({ settings }: { settings: SettingsType }) {
  const toast = useToast();
  const run = useAsyncAction(
    async () => {
      await bootstrapKeywords();
    },
    {
      successMessage: "Keyword bootstrap started",
      successDescription:
        "This takes a minute or two. Refresh this page to see the cluster count update.",
      onError: (e) => toast.error("Couldn't start bootstrap", e.message),
    }
  );
  return (
    <PanelCard
      title="Keyword universe"
      description="Discovered by scraping your website. The weekly cron and manual generation both pick topics from here."
    >
      <div className="grid grid-cols-2 gap-4 mb-5">
        <div className="rounded-lg border border-ink-100 bg-sand-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-500">
            Clusters
          </p>
          <p className="font-display text-3xl font-bold text-ink-800 tabular-nums mt-1">
            {settings.keyword_cluster_count}
          </p>
        </div>
        <div className="rounded-lg border border-ink-100 bg-sand-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-500">
            Seed keywords
          </p>
          <p className="font-display text-3xl font-bold text-ink-800 tabular-nums mt-1">
            {settings.seed_keywords.length}
          </p>
        </div>
      </div>
      {settings.keywords_bootstrapped_at && (
        <p className="text-sm text-ink-500 mb-4">
          Last refreshed{" "}
          {new Date(settings.keywords_bootstrapped_at).toLocaleString()}.
        </p>
      )}
      <Button
        variant="secondary"
        onClick={() => run.run()}
        pending={run.pending}
        iconLeft={<IconRefresh size={14} />}
      >
        {run.pending ? "Starting…" : "Refresh keywords from site"}
      </Button>
    </PanelCard>
  );
}
